import { DivisionConfiguration } from '@app/models/ebo/shared/division-configuration';
import { EboRoles } from '@app/models/ebo/shared/policies/ebo-roles.enum';
import { SystemId } from '@config/system-id';
import { PayrollFormat } from '@models/ebo/tenant/payroll-format.enum';
import { Tenant } from '@models/ebo/tenant/tenant';
import { TenantFeatures } from '@models/ebo/tenant/tenant-features';
import { IProfile } from '@summit/models';

import { assignSafe } from '@summit/core';
import { MultiCompanyLogic } from '../ebo/payroll/multi-company-logic.enum';
import { OtherParameters } from '../ebo/shared/other-parameters';
import { DigitalLogParameter } from '../ebo/shared/policies/digital-log-parameter';
import { RoleAccess } from '../ebo/shared/policies/role-access.enum';
import { UserExperience } from '../ebo/shared/user-experience';
import { DisplayConfiguration } from './display-configuration';
import { WageProfile } from './wage-profile';

export class Profile extends IProfile {
  tenant: Tenant;
  name: string;
  email: string;
  hubID: number;
  override roles: EboRoles;
  wage: WageProfile;
  systemAccount = false;

  //User Defaults
  truckAliasId: string;
  routeID: string;

  // Tenant Policies
  features: TenantFeatures;
  overrideReconcileLock: boolean;

  //Can't find in Server Employee View API Login
  delegatee: boolean;
  displayConfig: DisplayConfiguration;
  //Division Polices
  divisionConfig: DivisionConfiguration;

  //Management
  managedDivisionIds: string[];

  //Legacy Polices
  payrollFormat: PayrollFormat;

  private get digitalLogParameters(): DigitalLogParameter {
    return this.divisionConfig?.employeeParameters?.digitalLogParameters;
  }
  private set digitalLogParameters(value: DigitalLogParameter) {
    //NOP
  }
  private get otherParameters(): OtherParameters {
    return this.divisionConfig?.employeeParameters?.otherParameters;
  }
  private set otherParameters(value: OtherParameters) {
    //NOP
  }

  getDigitalLogParameters(): DigitalLogParameter | undefined {
    return this.digitalLogParameters;
  }

  constructor(jsonData?: Partial<Profile>) {
    super();
    //console.log(`Building Profile : `, jsonData);
    if (jsonData) {
      assignSafe(this, jsonData);
      if (jsonData.roles) this.roles = jsonData.roles;
      if (jsonData.wage) this.wage = new WageProfile(jsonData.wage);
      if (jsonData.tenant) this.tenant = new Tenant(jsonData.tenant);
      if (jsonData.features) this.features = new TenantFeatures(jsonData.features);
      if (jsonData.divisionConfig) this.divisionConfig = new DivisionConfiguration(jsonData.divisionConfig);
      if (jsonData.managedDivisionIds) this.managedDivisionIds = [...jsonData.managedDivisionIds];
    }
    this.roles ??= EboRoles.None;
    this.features ??= new TenantFeatures();
    this.managedDivisionIds ??= new Array<string>();

    //console.log(`Built Profile : `, this);
  }

  toShortString(): string {
    return `${this.tenant.abbreviation} - ${this.name}`;
  }

  isSystemTenant(): boolean {
    return this.tenantID.toLocaleUpperCase() == SystemId.SystemTenantID;
  }

  // //While you need to update this you also need to update UX Constructor
  //needed for AF_Node
  getUserExperience(): UserExperience {
    console.log(`[Profile] creating UX`, this);
    const response = new UserExperience({
      activeEmployeeID: this.id,
      activeEmployee: this,
      roles: this.roles,
      divisionConfig: new DivisionConfiguration(this?.divisionConfig),
      systemAccount: this.systemAccount,
      payrollFormat: this.payrollFormat,
      delegatee: this.delegatee,
      endOfWeek: this.tenant.endOfWeek,
      endOfScheduleWeek: this.tenant.endOfScheduleWeek,
      endOfIvmrWeek: this.tenant.endOfIvmrWeek,
      overrideReconcileLock: this.overrideReconcileLock,
      wage: this.wage,
      managedDivisionIds: this.managedDivisionIds
    });
    console.log('[Profile] Returning UX from profile', response);
    return response;
  }

  //Roles

  hasRole(flag: EboRoles): boolean {
    // eslint-disable-next-line no-bitwise
    return ((this.roles ?? EboRoles.None) & flag) > 0;
  }
  isSysAdmin(): boolean {
    return this.hasRole(EboRoles.SysAdmin);
  }
  isAdmin(): boolean {
    return this.hasRole(EboRoles.SysAdmin | EboRoles.Admin);
  }
  isPayroll(): boolean {
    return this.features?.payroll && this.hasRole(EboRoles.SysAdmin | EboRoles.Admin | EboRoles.Payroll);
  }
  isBackOffice(): boolean {
    return this.hasRole(EboRoles.SysAdmin | EboRoles.Admin | EboRoles.BackOffice);
  }
  isManager(): boolean {
    return this.hasRole(EboRoles.SysAdmin | EboRoles.Admin | EboRoles.BackOffice | EboRoles.Manager);
  }
  isManagerOnly(): boolean {
    return this.hasRole(EboRoles.Manager) && !this.isBackOffice();
  }
  isLeadDriver(): boolean {
    return this.hasRole(
      EboRoles.SysAdmin | EboRoles.Admin | EboRoles.BackOffice | EboRoles.Manager | EboRoles.LeadDriver
    );
  }
  isMechanic(): boolean {
    return this.hasRole(
      EboRoles.SysAdmin | EboRoles.Admin | EboRoles.BackOffice | EboRoles.Manager | EboRoles.Mechanic
    );
  }
  isDispatch(): boolean {
    return this.hasRole(
      EboRoles.SysAdmin | EboRoles.Admin | EboRoles.BackOffice | EboRoles.Manager | EboRoles.Dispatch
    );
  }
  isDriverOnly(): boolean {
    return this.roles === EboRoles.None;
  }

  showCompanySelection(): boolean {
    return (
      this.tenant.payroll.multiCompanyLogic == MultiCompanyLogic.BasedOnDaysLogs ||
      this.tenant.payroll.multiCompanyLogic == MultiCompanyLogic.NoChanges ||
      (this.isBackOffice() && this.tenant.payroll.multiCompanyLogic == MultiCompanyLogic.EmployeeCompanyWithOverride)
    );
  }

  public hasRoleOrBetter(testRole: EboRoles): boolean {
    switch (testRole) {
      case EboRoles.None:
        return true;
      case EboRoles.Manager:
        return this.isManager();
      case EboRoles.BackOffice:
        return this.isBackOffice();
      case EboRoles.Payroll:
        return this.isPayroll();
      case EboRoles.Admin:
        return this.isAdmin();
      case EboRoles.SysAdmin:
        return this.isSysAdmin();
      case EboRoles.Mechanic:
        return this.isMechanic();
      case EboRoles.LeadDriver:
        return this.isLeadDriver();
      case EboRoles.Dispatch:
        return this.isDispatch();
      default:
        return false;
    }
  }

  private canAccess(allowedRole: RoleAccess): boolean {
    switch (allowedRole) {
      case RoleAccess.All:
        return true;
      case RoleAccess.ManagerAndAbove:
        return this.isManager();
      case RoleAccess.BackOfficeAndAbove:
        return this.isBackOffice();
      case RoleAccess.Payroll:
        return this.isPayroll();
      case RoleAccess.Admin:
        return this.isAdmin();
      default:
        return false;
    }
  }

  //Access currently is their direct permissions
  //Report is for the division permissions
  canAccessFinancials(): boolean {
    return this.canAccess(this.otherParameters?.financialAccess);
  }
  canAccessDisputes(): boolean {
    return this.canAccess(this.otherParameters?.disputeAccess) || this.canAccess(this.otherParameters?.financialAccess);
  }
  canAccessTimeClock(): boolean {
    return this.features.driverLogs && this.wage?.timeClockEnabled;
  }
  canAccessLH(): boolean {
    return this.features.driverLogs && this.canAccess(this.digitalLogParameters?.linehaulAccess);
  }
  canReportLH(): boolean {
    return (
      this.features.driverLogs &&
      this.canAccess(this.divisionConfig?.reportParameters?.digitalLogParameters?.linehaulAccess)
    );
  }
  canAccessHourly(): boolean {
    return this.features.driverLogs && this.canAccess(this.digitalLogParameters?.hourlyAccess);
  }
  canReportHourly(): boolean {
    return (
      this.features.driverLogs &&
      this.canAccess(this.divisionConfig?.reportParameters?.digitalLogParameters?.hourlyAccess)
    );
  }
  canAccessSpots(): boolean {
    return this.features.driverLogs && this.canAccess(this.digitalLogParameters?.spotsAccess);
  }
  canReportSpots(): boolean {
    return (
      this.features.driverLogs &&
      this.canAccess(this.divisionConfig?.reportParameters?.digitalLogParameters?.spotsAccess)
    );
  }
  canAccessMiscWork(): boolean {
    return this.features.driverLogs && this.canAccess(this.digitalLogParameters?.miscWorkAccess);
  }
  canReportMiscWork(): boolean {
    return (
      this.features.driverLogs &&
      this.canAccess(this.divisionConfig?.reportParameters?.digitalLogParameters?.miscWorkAccess)
    );
  }
  canAccessExpense(): boolean {
    return this.features.driverLogs && this.canAccess(this.digitalLogParameters?.expenseAccess);
  }
  canReportExpense(): boolean {
    return (
      this.features.driverLogs &&
      this.canAccess(this.divisionConfig?.reportParameters?.digitalLogParameters?.expenseAccess)
    );
  }

  canAccessPrePostTrip(): boolean {
    return this.features.driverLogs && this.canAccess(this.digitalLogParameters?.prePostTripAccess);
  }
  canReportPrePostTrip(): boolean {
    return (
      this.features.driverLogs &&
      this.canAccess(this.divisionConfig?.reportParameters?.digitalLogParameters?.prePostTripAccess)
    );
  }

  canReportAdvances(): boolean {
    return (
      this.features.payroll && this.canAccess(this.divisionConfig?.reportParameters?.payRollParameters.advanceAccess)
    );
  }
  canReportHoliday(): boolean {
    return (
      this.features.payroll && this.canAccess(this.divisionConfig?.reportParameters?.payRollParameters.holidayPayAccess)
    );
  }
  canReportVacation(): boolean {
    return (
      this.features.payroll && this.canAccess(this.divisionConfig?.reportParameters?.payRollParameters.vacationAccess)
    );
  }
  canReportSickTime(): boolean {
    return (
      this.features.payroll && this.canAccess(this.divisionConfig?.reportParameters?.payRollParameters.sickTimeAccess)
    );
  }

  canAccessScheduling(): boolean {
    return this.features.scheduler && this.divisionConfig?.employeeParameters?.scheduleParameters?.schedulingEnabled;
  }
  canReportScheduling(): boolean {
    return this.features.scheduler && this.divisionConfig?.reportParameters?.scheduleParameters?.schedulingEnabled;
  }

  canAccessPnD(): boolean {
    return this.features.driverLogs && this.canAccess(this.digitalLogParameters?.pnDAccess);
  }
  canReportPnD(): boolean {
    return (
      this.features.driverLogs && this.canAccess(this.divisionConfig?.reportParameters?.digitalLogParameters?.pnDAccess)
    );
  }

  canEditTimeOff(): boolean {
    return (
      this.features.scheduler &&
      this.canAccess(this.divisionConfig?.employeeParameters?.scheduleParameters?.timeOffEdit)
    );
  }

  canEditRouteInfo(): boolean {
    return (
      this.features.scheduler &&
      this.canAccess(this.divisionConfig?.employeeParameters?.scheduleParameters?.routeInfoEdit)
    );
  }

  isCorporate(): boolean {
    return this.hubID === 0;
  }
}
